import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { VisualizationData } from '../../types';
import { formatNumber } from '../../lib/utils';

interface PieChartProps {
  data: VisualizationData;
  animated?: boolean;
}

const DEFAULT_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
];

export function PieChart({ data, animated }: PieChartProps) {
  const { config, data: chartData } = data;
  const { title, colors = DEFAULT_COLORS } = config;

  const nameField = chartData[0] ? Object.keys(chartData[0])[0] : 'name';
  const valueField = chartData[0] ? Object.keys(chartData[0])[1] : 'value';

  const total = chartData.reduce(
    (sum, item) => sum + (Number(item[valueField]) || 0),
    0
  );

  return (
    <div className="w-full">
      {title && (
        <h3 className="mb-4 text-sm font-medium text-foreground">{title}</h3>
      )}
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ value }) => {
                const percent = ((Number(value) / total) * 100).toFixed(1);
                return `${percent}%`;
              }}
              outerRadius={100}
              dataKey={valueField}
              nameKey={nameField}
              isAnimationActive={animated !== false}
            >
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => {
                const numericValue =
                  typeof value === 'number' ? value : Number(value);
                const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
                return [formatNumber(safeValue), name];
              }}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
              }}
            />
            <Legend
              formatter={(value) =>
                typeof value === 'string' && value.length > 20
                  ? value.substring(0, 20) + '...'
                  : value
              }
            />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
